import React, { memo } from 'react';
import { Field, FormikProps, useFormikContext } from 'formik';
import get from 'lodash/get';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';

import { FormFieldArrayRowFields } from '../../components/FormFieldArray/FormFieldArray';
import RenderInput from '../../components/RenderInput';
import rowFieldsStyles from '../rowFieldsStyles';
import TextFieldsOnBlur from '../../components/TextFieldsOnBlur';
import { getToFixedNumber, taytanFormatNumberToString } from '../../utils/mathUtils';
import { sameCurrency } from '../../utils/currencyMath';

export const getEmptyCheck = () => {
    return { bank: '', branch: '', account: '', number: '', amount: '', valueDate: null };
};

export type CheckField = {
    bank: string;
    branch: string;
    account: string;
    number: string;
    amount: number;
    valueDate: Date | null;
};

/** Header summary for a collapsed check row in FormFieldArray. */
export const getCheckSummary =
    (currencySymbol = '') =>
    (row: any, index: number): string => {
        const parts = [`#${index + 1}`];
        if (row?.bank) parts.push(row.bank);
        if (row?.number) parts.push(`Check ${row.number}`);
        if (row?.amount) parts.push(`${currencySymbol}${row.amount}`);
        if (row?.valueDate) {
            const d = new Date(row.valueDate);
            if (!isNaN(d.getTime())) parts.push(d.toLocaleDateString('en-GB'));
        }
        return parts.join(' · ');
    };

export type ChecksFieldArray = {
    checks: CheckField[];
};

/**
 * Builds the suffix shown next to a check's Amount label — the same money
 * expressed in the client's LOCAL currency.
 *
 * `rate` is a document→local rate only while the document is in a FOREIGN
 * currency. For a local-currency document RateControlBox deliberately
 * repurposes it as the local↔PRIMARY rate (₪ per $), so multiplying by it
 * printed a dollar-scaled number under a shekel sign — a 100₪ check read
 * "Amount (308₪)". A local check is already local: there is nothing to
 * convert, so show no equivalent at all.
 *
 * Returns '' when there is nothing meaningful to show; RowCheckFields then
 * renders a plain "Amount".
 */
export const getCheckLocalAmountLabel =
    (documentCurrency: unknown, localCurrency: unknown, rate?: number | null, localSymbol = '') =>
    (checkAmount: number): string => {
        const documentCurrencyId =
            typeof documentCurrency === 'object'
                ? (documentCurrency as { _id?: string })?._id
                : documentCurrency;
        if (!documentCurrencyId || sameCurrency(documentCurrencyId, localCurrency)) return '';
        return `${taytanFormatNumberToString(checkAmount * (rate || 1), 0)}${localSymbol}`;
    };

const required = true;
const RowCheckFields = ({ index, fieldName, propsToRowFields }: FormFieldArrayRowFields) => {
    const classes = rowFieldsStyles();
    const formProps = useFormikContext() as FormikProps<any>;
    const { values, setFieldValue } = formProps;

    // Empty means "no local equivalent worth showing" (e.g. a check already in
    // the local currency) — render a plain "Amount", not "Amount ()".
    const extraAmountLabel: string =
        propsToRowFields?.getExtraRowAmountLabel?.(get(values, `${fieldName}[${index}].amount`, '')) ||
        '';

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextFieldsOnBlur
                        fields={[
                            { name: `${fieldName}[${index}].bank`, label: 'Bank', required, fullWidth: true }
                        ]}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextFieldsOnBlur
                        fields={[{ name: `${fieldName}[${index}].branch`, label: 'Branch', fullWidth: true }]}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextFieldsOnBlur
                        fields={[
                            {
                                name: `${fieldName}[${index}].account`,
                                label: 'Account',
                                required,
                                fullWidth: true
                            }
                        ]}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextFieldsOnBlur
                        fields={[
                            {
                                name: `${fieldName}[${index}].number`,
                                label: 'Check Number',
                                required,
                                fullWidth: true
                            }
                        ]}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        type="number"
                        component={RenderInput}
                        label={`Amount${extraAmountLabel ? ` (${extraAmountLabel})` : ''}`}
                        field={{
                            name: `${fieldName}[${index}].amount`,
                            onBlur: (ev: any) => {
                                setFieldValue(`${fieldName}[${index}].amount`, Number(ev.target.value));
                            }
                        }}
                        defaultValue={
                            getToFixedNumber(get(values, `${fieldName}[${index}].amount`, '')) || ''
                        }
                        key={`amount${get(values, `${fieldName}[${index}].amount`, '')}`}
                        placeholder="0"
                        startAdornment={
                            <InputAdornment position="start">
                                {propsToRowFields.currencySymbol}
                            </InputAdornment>
                        }
                        fullWidth
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        type="date"
                        component={RenderInput}
                        name={`${fieldName}[${index}].valueDate`}
                        label="Value Date"
                        autoOk
                        placeholder="__/__/____"
                        mask="__/__/____"
                        format="dd/MM/yyyy"
                        helperText=" "
                        InputAdornmentProps={{ position: 'start' }}
                        InputProps={{
                            endAdornment: get(values, `${fieldName}[${index}].valueDate`) ? (
                                <IconButton
                                    className={classes.padding2px}
                                    onClick={() => setFieldValue(`${fieldName}[${index}].valueDate`, null)}
                                    size="large"
                                >
                                    <ClearIcon />
                                </IconButton>
                            ) : null
                        }}
                        fullWidth
                        required
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default memo(RowCheckFields);
